import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "Components/PageHeader";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import {
  SbButton,
  SbCard,
  SbInput,
  SbTabs,
  SbDropdown,
  SbNotification,
  SbTooltip,
  TooltipTriggerStyled,
} from "uiCore";
import { CaretDownOutlined, EditOutlined } from "uiCore";
import useAuthStore from "Store/useAuthStore";
import useEntitlementsStore from "Store/useEntitlementsStore";
import usePAICStore from "Store/usePAICStore";

import {
  getPAICAgentDetails,
  getPAICAgents,
  updatePAICAgent,
} from "Api/apiService";

import Permission from "Components/Permission";
import analytics from "Utils/analytics";
import { PAIC_ROUTES } from "constants/paicRoutes.constants";

import PACGroupAgentDetailsTableV2 from "./PACGroupAgentDetailsTableV2";
import {
  GROUP_DETAIL_TABS,
  PEOPLE_PLUS_AI_MENU_ITEMS,
} from "constants/groupAgents.constants";
import {
  PAIC_STATS_PRIMARY_ITEMS,
  PAIC_STATS_SECONDARY_ITEMS,
  PAIC_PROJECT_SOURCE,
  PAIC_VIEW_ONLY_MESSAGE,
  handlePAICErrorCode,
} from "constants/peoplePlusAI.constants";

import {
  PageContainer,
  HeaderSection,
  TabsAndControlsBlock,
  ContentSection,
  MainContent,
  SidebarContent,
  DetailCardShell,
  SectionHeader,
  AgentCodeBadge,
  MetaStack,
  MetaRow,
  MetaLabel,
  MetaValueWrap,
  EditIconButton,
  EditFieldBlock,
  ActionButtonsRow,
  TableSection,
  StatsCardContainer,
  StatsCardHeader,
  StatsCardTitle,
  StatsCardSubtitle,
  StatsCardBody,
  StatsPrimaryRow,
  StatsBigValue,
  StatsLabel,
  StatsRoiBadge,
  StatsTileGrid3,
  StatsTileGrid2,
  StatsTile,
  StatsTileLabel,
  StatsTileValue,
  ComingSoonBlock,
  TabLabelWithCaret,
} from "./SbPACGroupAgentDetailsV2.styles";

// PRD §5.12 — tooltip content strings for Agent Details
const TOOLTIP_BACK = "Return to the Group Agents screen.";
const TOOLTIP_EDIT_NAME =
  "Edit the AI-generated name of this agent to better reflect your organisation's terminology.";
const TOOLTIP_EDIT_DESCRIPTION =
  "Edit the AI-generated description of this agent. The description is used in the Agent JD and PRD documents.";

const { Title, Text, Paragraph } = Typography;

const StatsSummaryCard = ({
  title,
  subtitle,
  potentialValue,
  roiValue,
  rolesCount,
  positionsCount,
  agentsCount,
  aiPotential,
  estimatedCost,
  hasCompEntitlement = true,
}) => {
  const primaryStatsMap = {
    roles: rolesCount ?? 0,
    positions: positionsCount ?? 0,
    agents: agentsCount ?? 0,
  };

  const secondaryStatsMap = {
    aiPotential,
    estimatedCost,
  };

  return (
    <StatsCardContainer>
      <SbCard
        className="paic-stats-card"
        bordered={false}
        title={
          <StatsCardHeader>
            <StatsCardTitle>{title}</StatsCardTitle>
            {subtitle ? (
              <StatsCardSubtitle>{subtitle}</StatsCardSubtitle>
            ) : null}
          </StatsCardHeader>
        }
      >
        <StatsCardBody>
          <StatsPrimaryRow>
            <div>
              <StatsBigValue>{hasCompEntitlement ? potentialValue : "-"}</StatsBigValue>
              <StatsLabel>Potential Value</StatsLabel>
            </div>
            <StatsRoiBadge>
              {hasCompEntitlement ? roiValue : "ROI -"}
            </StatsRoiBadge>
          </StatsPrimaryRow>

          <StatsTileGrid3>
            {PAIC_STATS_PRIMARY_ITEMS.map(({ key, label }) => (
              <StatsTile key={key}>
                <StatsTileLabel>{label}</StatsTileLabel>
                <StatsTileValue>{primaryStatsMap[key]}</StatsTileValue>
              </StatsTile>
            ))}
          </StatsTileGrid3>

          <StatsTileGrid2>
            {PAIC_STATS_SECONDARY_ITEMS.map(({ key, label }) => (
              <StatsTile key={key}>
                <StatsTileLabel>{label}</StatsTileLabel>
                <StatsTileValue>
                  {key === "estimatedCost" && !hasCompEntitlement
                    ? "-"
                    : secondaryStatsMap[key]}
                </StatsTileValue>
              </StatsTile>
            ))}
          </StatsTileGrid2>
        </StatsCardBody>
      </SbCard>
    </StatsCardContainer>
  );
};

const SbPACGroupAgentDetailsV2 = () => {
  const navigate = useNavigate();

  const email = useAuthStore((s) => s.email);
  const entitlements = useEntitlementsStore((s) => s.entitlements);

  const hasCompEntitlement = entitlements
    ?.find((e) => e.name === "people_plus_ai")
    ?.entitlements?.includes("view_comp");

  const projectId = usePAICStore((s) => s.projectId);
  const projectSource = usePAICStore((s) => s.projectSource);
  const loading = usePAICStore((s) => s.loading);
  const setLoading = usePAICStore((s) => s.setLoading);
  const groupName = usePAICStore((s) => s.groupName);
  const agentId = usePAICStore((s) => s.agentId);

  const isReadOnly = projectSource === PAIC_PROJECT_SOURCE.SHARED;

  const [activeTab, setActiveTab] = useState("people-plus-ai");
  const [agentTasks, setAgentTasks] = useState([]);
  const [agentInfo, setAgentInfo] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");

  const showSuccessNotification = (message, description = "") => {
    SbNotification.success(message, description);
  };

  const showErrorNotification = (message, description = "") => {
    SbNotification.error(message, description);
  };

  const formatCurrencyShort = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return "$0";
    return `$ ${Math.round(num).toLocaleString()}`;
  };

  const formatPercentage = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return "0%";
    return `${(num * 100).toFixed(1)}%`;
  };

  const taskRows = useMemo(
    () =>
      (agentTasks || []).map((task, index) => ({
        ...task,
        __rowKey:
          task?.sb_unique_id ||
          task?.sb_agent_detail_unique_id ||
          `${task?.parent_task || "task"}-${index}`,
        __rowIndex: index,
      })),
    [agentTasks],
  );

  const loadPageData = async () => {
    if (!agentId || !projectId) {
      setAgentTasks([]);
      setAgentInfo(null);
      return;
    }

    setLoading(true);

    try {
      const [tasksResponse, agentsResponse] = await Promise.all([
        getPAICAgentDetails(agentId),
        getPAICAgents(projectId),
      ]);

      setAgentTasks(tasksResponse || []);

      const agents = agentsResponse?.data || [];
      const foundAgent = agents.find(
        (item) => item?.sb_agent_unique_id === agentId,
      );

      setAgentInfo(foundAgent || null);
    } catch (error) {
      if (!handlePAICErrorCode(error, showErrorNotification)) {
        showErrorNotification(
          "Unable to load agent details",
          "Please refresh the page and try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, [agentId, projectId]);

  useEffect(() => {
    if (!agentInfo) return;

    analytics.trackPAICAgentDetailsViewed({
      from: "pac_agent_details",
      email,
      agentId,
      agentName: agentInfo?.ai_agent_name,
      description: `Viewed details for agent: ${agentInfo?.ai_agent_name}`,
    });
  }, [agentInfo, email, agentId]);

  const handleOnBackClick = () => {
    analytics.trackPAICCloseAgentDetails({
      from: "pac_agent_details",
      email,
      description: "Closed agent details view",
    });

    navigate(PAIC_ROUTES.GROUP_AGENTS, {
      state: { fromParent: true },
    });
  };

  const handleTabClick = (tabKey) => {
    setActiveTab(tabKey);
  };

  const handlePeopleAIMenuClick = ({ key }) => {
    setActiveTab("people-plus-ai");

    if (key === "for-role") {
      SbNotification.info("For Role view is coming soon");
    }
  };

  const tabItems = GROUP_DETAIL_TABS.map((tab) => {
    // TODO: Dropdown removed temporarily - no functionality implemented
    // if (tab.key === "people-plus-ai") {
    //   return {
    //     key: tab.key,
    //     label: (
    //       <SbDropdown
    //         menu={{
    //           items: PEOPLE_PLUS_AI_MENU_ITEMS,
    //           onClick: handlePeopleAIMenuClick,
    //         }}
    //         trigger={["click"]}
    //       >
    //         <TabLabelWithCaret onClick={() => setActiveTab("people-plus-ai")}>
    //           {tab.label}
    //           <CaretDownOutlined />
    //         </TabLabelWithCaret>
    //       </SbDropdown>
    //     ),
    //   };
    // }

    return { key: tab.key, label: tab.label };
  });

  const handleSbTabsChange = (tabKey) => {
    if (tabKey === "people-plus-ai") {
      setActiveTab("people-plus-ai");
      return;
    }

    handleTabClick(tabKey);
  };

  const toggleExpandedRow = (record) => {
    const rowKey = record?.__rowKey;
    const rowIndex = record?.__rowIndex;

    setExpandedRowKeys((prev) => {
      const isExpanded = prev.includes(rowKey);

      if (isExpanded) {
        analytics.trackPAICCollapseRoleDetail({
          from: "pac_agent_details",
          email,
          roleIndex: rowIndex,
          description: `Collapsed role detail at index ${rowIndex}`,
        });

        return prev.filter((key) => key !== rowKey);
      }

      analytics.trackPAICExpandRoleDetail({
        from: "pac_agent_details",
        email,
        roleIndex: rowIndex,
        description: `Expanded role detail at index ${rowIndex}`,
      });

      return [...prev, rowKey];
    });
  };

  const showViewOnlyNotification = () => {
    SbNotification.info(PAIC_VIEW_ONLY_MESSAGE.title, PAIC_VIEW_ONLY_MESSAGE.description);
  };

  const handleEditNameClick = () => {
    if (isReadOnly) { showViewOnlyNotification(); return; }
    setEditedName(agentInfo?.ai_agent_name || "");
    setIsEditingName(true);

    analytics.trackPAICEditAgentName({
      from: "pac_agent_details",
      email,
      agentId,
      description: "Started editing agent name",
    });
  };

  const handleCancelNameEdit = () => {
    setIsEditingName(false);
    setEditedName("");

    analytics.trackPAICCancelAgentNameEdit({
      from: "pac_agent_details",
      email,
      agentId,
      description: "Cancelled agent name edit",
    });
  };

  const handleSaveNameEdit = async () => {
    const nextName = editedName.trim();

    if (!nextName) {
      showErrorNotification("Agent name cannot be empty");
      return;
    }

    try {
      setLoading(true);

      await updatePAICAgent(agentId, {
        ai_agent_name: nextName,
      });

      analytics.trackPAICSaveAgentName({
        from: "pac_agent_details",
        email,
        agentId,
        oldName: agentInfo?.ai_agent_name,
        newName: nextName,
        description: `Updated agent name from "${agentInfo?.ai_agent_name}" to "${nextName}"`,
      });

      setAgentInfo((prev) =>
        prev
          ? {
              ...prev,
              ai_agent_name: nextName,
            }
          : prev,
      );

      setIsEditingName(false);
      showSuccessNotification("Agent name updated successfully");
    } catch (error) {
      if (!handlePAICErrorCode(error, showErrorNotification)) {
        showErrorNotification("Failed to update agent name", "Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditDescriptionClick = () => {
    if (isReadOnly) { showViewOnlyNotification(); return; }
    setEditedDescription(agentInfo?.ai_agent_description || "");
    setIsEditingDescription(true);

    analytics.trackPAICEditAgentDescription({
      from: "pac_agent_details",
      email,
      agentId,
      description: "Started editing agent description",
    });
  };

  const handleCancelDescriptionEdit = () => {
    setIsEditingDescription(false);
    setEditedDescription("");

    analytics.trackPAICCancelAgentDescriptionEdit({
      from: "pac_agent_details",
      email,
      agentId,
      description: "Cancelled agent description edit",
    });
  };

  const handleSaveDescriptionEdit = async () => {
    try {
      setLoading(true);

      await updatePAICAgent(agentId, {
        ai_agent_description: editedDescription,
      });

      analytics.trackPAICSaveAgentDescription({
        from: "pac_agent_details",
        email,
        agentId,
        description: "Updated agent description",
      });

      setAgentInfo((prev) =>
        prev
          ? {
              ...prev,
              ai_agent_description: editedDescription,
            }
          : prev,
      );

      setIsEditingDescription(false);
      showSuccessNotification("Agent description updated successfully");
    } catch (error) {
      if (!handlePAICErrorCode(error, showErrorNotification)) {
        showErrorNotification(
          "Failed to update agent description",
          "Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const renderComingSoon = (label) => (
    <ComingSoonBlock>
      <Text type="secondary">{label} is coming soon.</Text>
    </ComingSoonBlock>
  );

  return (
    <PageContainer>
      <PageHeader
        title={`Group: ${groupName || "N/A"}`}
        onBack={handleOnBackClick}
        backLabel={TOOLTIP_BACK}
      />

      <HeaderSection>
        <TabsAndControlsBlock>
          <SbTabs
            activeKey={activeTab}
            onChange={handleSbTabsChange}
            items={tabItems}
            size="large"
          />
        </TabsAndControlsBlock>
      </HeaderSection>

      <ContentSection>
        <MainContent>
          {activeTab === "people-plus-ai" && (
            <DetailCardShell>
              <SbCard className="paic-detail-card" bordered={false}>
                <SectionHeader>
                  <div>
                    <Title level={4} className="paic-section-title">
                      Agent Tasks and Microtasks
                    </Title>
                    <Text type="secondary">
                      Review the agent summary, rename it if needed, and explore
                      the expandable task breakdown below.
                    </Text>
                  </div>

                  <AgentCodeBadge>
                    Agent Code: {agentInfo?.sb_agent_code || "N/A"}
                  </AgentCodeBadge>
                </SectionHeader>

                <MetaStack>
                  <MetaRow>
                    <MetaLabel>Name</MetaLabel>

                    {!isEditingName ? (
                      <>
                        <MetaValueWrap>
                          <Text className="paic-meta-value">
                            {agentInfo?.ai_agent_name || "Loading..."}
                          </Text>
                        </MetaValueWrap>

                        <Permission
                          resource="people_plus_ai"
                          actions={["edit"]}
                        >
                          <SbTooltip content={isReadOnly ? PAIC_VIEW_ONLY_MESSAGE.description : TOOLTIP_EDIT_NAME}>
                            <TooltipTriggerStyled>
                              <EditIconButton
                                type="button"
                                onClick={handleEditNameClick}
                                disabled={loading}
                              >
                                <EditOutlined />
                              </EditIconButton>
                            </TooltipTriggerStyled>
                          </SbTooltip>
                        </Permission>
                      </>
                    ) : (
                      <EditFieldBlock>
                        <SbInput
                          value={editedName}
                          disabled={loading}
                          autoFocus
                          placeholder="Enter agent name"
                          onChange={(e) => setEditedName(e.target.value)}
                          onPressEnter={handleSaveNameEdit}
                        />

                        <ActionButtonsRow>
                          <SbButton
                            size="small"
                            onClick={handleCancelNameEdit}
                            disabled={loading}
                          >
                            Cancel
                          </SbButton>
                          <SbButton
                            buttonType="primary"
                            size="small"
                            onClick={handleSaveNameEdit}
                            loading={loading}
                          >
                            Save
                          </SbButton>
                        </ActionButtonsRow>
                      </EditFieldBlock>
                    )}
                  </MetaRow>

                  <MetaRow $alignTop>
                    <MetaLabel>Description</MetaLabel>

                    {!isEditingDescription ? (
                      <>
                        <MetaValueWrap>
                          <Paragraph className="paic-meta-paragraph">
                            {agentInfo?.ai_agent_description || "N/A"}
                          </Paragraph>
                        </MetaValueWrap>

                        <Permission
                          resource="people_plus_ai"
                          actions={["edit"]}
                        >
                          <SbTooltip content={isReadOnly ? PAIC_VIEW_ONLY_MESSAGE.description : TOOLTIP_EDIT_DESCRIPTION}>
                            <TooltipTriggerStyled>
                              <EditIconButton
                                type="button"
                                onClick={handleEditDescriptionClick}
                                disabled={loading}
                              >
                                <EditOutlined />
                              </EditIconButton>
                            </TooltipTriggerStyled>
                          </SbTooltip>
                        </Permission>
                      </>
                    ) : (
                      <EditFieldBlock>
                        <SbInput
                          textarea
                          rows={3}
                          value={editedDescription}
                          disabled={loading}
                          placeholder="Enter agent description"
                          onChange={(e) => setEditedDescription(e.target.value)}
                        />

                        <ActionButtonsRow>
                          <SbButton
                            size="small"
                            onClick={handleCancelDescriptionEdit}
                            disabled={loading}
                          >
                            Cancel
                          </SbButton>
                          <SbButton
                            buttonType="primary"
                            size="small"
                            onClick={handleSaveDescriptionEdit}
                            loading={loading}
                          >
                            Save
                          </SbButton>
                        </ActionButtonsRow>
                      </EditFieldBlock>
                    )}
                  </MetaRow>
                </MetaStack>

                <TableSection>
                  <PACGroupAgentDetailsTableV2
                    loading={loading}
                    rows={taskRows}
                    expandedRowKeys={expandedRowKeys}
                    onToggleRow={toggleExpandedRow}
                    formatPercentage={formatPercentage}
                  />
                </TableSection>
              </SbCard>
            </DetailCardShell>
          )}

          {activeTab === "learning-catalyst" &&
            renderComingSoon("Learning Catalyst")}
          {activeTab === "talent-mobility" &&
            renderComingSoon("Talent Mobility")}
          {activeTab === "jd" && renderComingSoon("Jobs Library")}
          {activeTab === "skills-bridger" && renderComingSoon("Skills Bridger")}
        </MainContent>

        {activeTab === "people-plus-ai" && (
          <SidebarContent>
            <StatsSummaryCard
              title="AGENT TOTAL"
              subtitle="Agent Impact"
              hasCompEntitlement={hasCompEntitlement}
              potentialValue={formatCurrencyShort(
                agentInfo?.total_value_to_business_c || 0,
              )}
              roiValue={`ROI ${(Number(agentInfo?.ROI_C) || 0).toFixed(1)}`}
              rolesCount={agentInfo?.no_of_roles_impacted}
              positionsCount={agentInfo?.no_of_positions_impacted}
              agentsCount={1}
              aiPotential={formatPercentage(
                agentInfo?.ai_percent_total_base_c || 0,
              )}
              estimatedCost={formatCurrencyShort(
                agentInfo?.estimated_development_cost || 0,
              )}
            />
          </SidebarContent>
        )}
      </ContentSection>
    </PageContainer>
  );
};

export default SbPACGroupAgentDetailsV2;
